import type { PriceChangeItem } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Badge per change_type
// ---------------------------------------------------------------------------

const CHANGE_TYPE_LABEL: Record<string, string> = {
  create: "Nuova pubblicazione",
  update: "Aggiornamento",
  manual: "Manuale",
  failed: "Fallito",
  skipped: "Saltato",
};

// Solo classi esistenti nel design system (index.css): pill-ok | pill-neutral
// | pill-out | pill-risk. create=positivo (verde), failed=negativo (rosso),
// manual=da-notare (risk), update/skipped=neutro.
const CHANGE_TYPE_CLASS: Record<string, string> = {
  create: "pill-ok",
  update: "pill-neutral",
  manual: "pill-risk",
  failed: "pill-out",
  skipped: "pill-neutral",
};

// ---------------------------------------------------------------------------
// Dot esito
// ---------------------------------------------------------------------------

function EsitoDot({ success, id }: { success: boolean | null; id: number }): JSX.Element {
  const cls =
    success === true
      ? "dot dot-ok"
      : success === false
        ? "dot dot-err"
        : "dot dot-neutral";
  return <span className={cn("inline-block h-2 w-2 rounded-full", cls)} data-testid={`esito-dot-${id}`} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Riga prezzo old → new
// ---------------------------------------------------------------------------

function PriceCell({ item }: { item: PriceChangeItem }): JSX.Element {
  const { old_price, new_price, id } = item;
  const hasOld = old_price !== null && old_price !== undefined;
  const hasNew = new_price !== null && new_price !== undefined;

  if (!hasOld && !hasNew) {
    return (
      <span className="font-mono tabular-nums text-xs text-ink-3" data-testid={`price-cell-${id}`}>
        —
      </span>
    );
  }

  return (
    <span className="font-mono tabular-nums text-xs" data-testid={`price-cell-${id}`}>
      {hasOld && (
        <span className="text-ink-3">{formatPrice(old_price)}</span>
      )}
      {hasOld && hasNew && (
        <span className="mx-1 text-ink-3" aria-hidden="true">→</span>
      )}
      {hasNew && (
        <span className="font-medium">{formatPrice(new_price)}</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tabella principale
// ---------------------------------------------------------------------------

interface PriceChangesTableProps {
  items: PriceChangeItem[];
}

export function PriceChangesTable({ items }: PriceChangesTableProps): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="card-stack flex h-40 items-center justify-center bg-card text-sm text-ink-3">
        Nessuna variazione trovata con i filtri correnti.
      </div>
    );
  }

  return (
    <div className="card-stack overflow-auto bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-2 text-label font-semibold text-ink-3">
            <th className="px-4 py-2.5 text-left font-semibold">Quando</th>
            <th className="px-4 py-2.5 text-left font-semibold">Canale</th>
            <th className="px-4 py-2.5 text-left font-semibold">SKU</th>
            <th className="px-4 py-2.5 text-left font-semibold">Tipo</th>
            <th className="px-4 py-2.5 text-left font-semibold">Prezzo</th>
            <th className="px-4 py-2.5 text-center font-semibold">Esito</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr
              key={item.id}
              className={cn(
                "border-b border-line transition hover:bg-key/15",
                idx % 2 === 1 && "bg-paper-2",
              )}
            >
              <td className="px-4 py-3 font-mono tabular-nums text-xs text-ink-3">
                {new Date(item.ts).toLocaleString("it-IT")}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-ink-3 truncate max-w-[160px]">
                {item.marketplace_id ?? "—"}
              </td>
              <td className="px-4 py-3 font-code text-xs truncate max-w-[140px]">
                {item.sku ?? "—"}
              </td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    "pill text-xs",
                    CHANGE_TYPE_CLASS[item.change_type] ?? "pill-neutral",
                  )}
                >
                  {CHANGE_TYPE_LABEL[item.change_type] ?? item.change_type}
                </span>
              </td>
              <td className="px-4 py-3">
                <PriceCell item={item} />
              </td>
              <td className="px-4 py-3 text-center">
                <EsitoDot success={item.success} id={item.id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
